class MainSearch extends SearchForm {
  constructor() {
    super();

    this.allSearchInputs = document.querySelectorAll(
      'input[type="search"]'
    );

    this.perPage = 24;
    this.isSearchFiltering = false;

    this.setupEventListeners();

    // Search page only
    if (
      document.querySelector('.template-search') &&
      this.input?.value?.trim()
    ) {
      this.initSearchFilter();
    }
  }

  setupEventListeners() {
    let allSearchForms = [];

    this.allSearchInputs.forEach((input) => {
      allSearchForms.push(input.form);
    });

    this.input.addEventListener(
      'focus',
      this.onInputFocus.bind(this)
    );

    if (allSearchForms.length < 2) return;

    allSearchForms.forEach((form) =>
      form.addEventListener(
        'reset',
        this.onFormReset.bind(this)
      )
    );

    this.allSearchInputs.forEach((input) =>
      input.addEventListener(
        'input',
        this.onInput.bind(this)
      )
    );
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.keepInSync('', this.input);
    }
  }

  onInput(event) {
    const target = event.target;
    this.keepInSync(target.value, target);
  }

  onInputFocus() {
    const isSmallScreen = window.innerWidth < 750;

    if (isSmallScreen) {
      this.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }

  keepInSync(value, target) {
    this.allSearchInputs.forEach((input) => {
      if (input !== target) {
        input.value = value;
      }
    });
  }

  async initSearchFilter() {
    if (this.isSearchFiltering) return;

    this.isSearchFiltering = true;

    const query = this.input.value.trim();

    if (!query) {
      this.isSearchFiltering = false;
      return;
    }

    try {
      const allResults = await this.fetchAllSearchResults(query);

      const visibleResults = allResults.filter((item) => {
        return !item.querySelector('.HideSearch');
      });

      this.updateSearchResults(visibleResults);

    } catch (error) {
      console.error('Search filter error:', error);
    }

    this.isSearchFiltering = false;
  }

  async fetchAllSearchResults(query) {
    const allResults = [];
    const seenUrls = new Set();

    let page = 1;
    let hasMorePages = true;

    while (hasMorePages) {
      const url =
        `${window.Shopify.routes.root}search` +
        `?q=${encodeURIComponent(query)}` +
        `&options[prefix]=last` +
        `&page=${page}`;

      const response = await fetch(url);

      if (!response.ok) {
        break;
      }

      const html = await response.text();

      const parser = new DOMParser();

      const doc = parser.parseFromString(
        html,
        'text/html'
      );

      const products = [
        ...doc.querySelectorAll(
          '#product-grid .grid__item'
        )
      ];

      if (!products.length) {
        hasMorePages = false;
        break;
      }

      let newResults = 0;

      products.forEach((item) => {
        const link = item.querySelector(
          'a[href]'
        );

        const itemUrl = link?.href || '';

        if (!seenUrls.has(itemUrl)) {
          seenUrls.add(itemUrl);
          allResults.push(item);
          newResults++;
        }
      });

      if (
        newResults === 0 ||
        products.length < this.perPage
      ) {
        hasMorePages = false;
      } else {
        page++;
      }

      // Safety limit
      if (page > 50) {
        hasMorePages = false;
      }
    }

    return allResults;
  }

  updateSearchResults(results) {
    const grid = document.querySelector(
      '#product-grid .product-grid'
    );

    if (!grid) return;

    const currentPage =
      this.getCurrentPage();

    const totalResults = results.length;

    const totalPages = Math.ceil(
      totalResults / this.perPage
    );

    let page = currentPage;

    if (page > totalPages && totalPages > 0) {
      page = totalPages;
    }

    const start =
      (page - 1) * this.perPage;

    const end =
      start + this.perPage;

    const currentResults =
      results.slice(start, end);

    /*
     * Update products
     */
    grid.innerHTML = '';

    currentResults.forEach((item) => {
      grid.appendChild(
        item.cloneNode(true)
      );
    });

    /*
     * Update result count
     */
    this.updateResultCount(totalResults);

    /*
     * Update pagination
     */
    this.updatePagination(
      totalPages,
      page
    );

    /*
     * Update URL if required
     */
    if (page !== currentPage) {
      this.updateUrl(page);
    }
  }

  updateResultCount(count) {
    /*
     * Normal search count
     */
    const countElements =
      document.querySelectorAll(
        '[role="status"]'
      );

    countElements.forEach((element) => {
      const text = element.textContent.trim();

      if (
        text.includes('result') ||
        text.includes('Results') ||
        text.includes('results')
      ) {
        element.textContent =
          `${count} results`;
      }
    });

    /*
     * Facet result count
     */
    const desktopCount =
      document.querySelector(
        '#ProductCountDesktop'
      );

    if (desktopCount) {
      desktopCount.textContent =
        `${count} results`;
    }

    /*
     * Mobile count
     */
    const mobileCount =
      document.querySelector(
        '#ProductCount'
      );

    if (mobileCount) {
      mobileCount.textContent =
        `${count} results`;
    }
  }

  updatePagination(totalPages, currentPage) {
    const pagination =
      document.querySelector(
        '#product-grid .pagination'
      );

    if (!pagination) return;

    /*
     * Remove old pagination
     */
    pagination.innerHTML = '';

    if (totalPages <= 1) {
      pagination.closest(
        '.pagination-wrapper'
      )?.remove();

      return;
    }

    /*
     * Previous
     */
    if (currentPage > 1) {
      const prev =
        document.createElement('a');

      prev.href =
        this.getPageUrl(
          currentPage - 1
        );

      prev.className =
        'pagination__item pagination__item--prev';

      prev.innerHTML = '‹';

      pagination.appendChild(prev);
    }

    /*
     * Page numbers
     */
    for (
      let page = 1;
      page <= totalPages;
      page++
    ) {
      const link =
        document.createElement('a');

      link.href =
        this.getPageUrl(page);

      link.className =
        'pagination__item';

      link.textContent = page;

      if (page === currentPage) {
        link.classList.add(
          'pagination__item--current'
        );

        link.setAttribute(
          'aria-current',
          'page'
        );
      }

      pagination.appendChild(link);
    }

    /*
     * Next
     */
    if (currentPage < totalPages) {
      const next =
        document.createElement('a');

      next.href =
        this.getPageUrl(
          currentPage + 1
        );

      next.className =
        'pagination__item pagination__item--next';

      next.innerHTML = '›';

      pagination.appendChild(next);
    }
  }

  getCurrentPage() {
    const params =
      new URLSearchParams(
        window.location.search
      );

    return parseInt(
      params.get('page'),
      10
    ) || 1;
  }

  getPageUrl(page) {
    const url =
      new URL(
        window.location.href
      );

    if (page <= 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set(
        'page',
        page
      );
    }

    return url.toString();
  }

  updateUrl(page) {
    const url =
      new URL(
        window.location.href
      );

    if (page <= 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set(
        'page',
        page
      );
    }

    window.history.replaceState(
      {},
      '',
      url.toString()
    );
  }
}

customElements.define(
  'main-search',
  MainSearch
);